import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { COOP_STATUS } from '../lib/coopMembers';

export default function useCoopMemberRole(shouldFetch = true) {
  const [coopRole, setCoopRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: currentUser } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Memoize the role checking results to prevent unnecessary re-renders
  // Note: Delivery role is handled separately from website users, not from members collection
  const roleChecks = React.useMemo(() => {
    if (!shouldFetch || loading || !coopRole) {
      return {
        isAdmin: false,
        isFinancial: false
      };
    }
    
    return {
      isAdmin: ['admin'].includes(coopRole),
      isFinancial: ['treasurer', 'cashier'].includes(coopRole)
    };
  }, [shouldFetch, loading, coopRole]);

  useEffect(() => {
    if (!shouldFetch || !currentUser?.uid) {
      setCoopRole(null);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;
    let timeoutId = null;

    const fetchCoopRole = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Add timeout to prevent hanging
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('Role fetch timeout, using fallback');
            setCoopRole(null);
            setLoading(false);
          }
        }, 10000); // 10 second timeout
        
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const db = getFirestore();
        
        const memberDoc = await getDoc(doc(db, 'members', currentUser.uid));
        
        if (!isMounted) return;
        
        clearTimeout(timeoutId);
        
        if (memberDoc.exists()) {
          const memberData = memberDoc.data();
          // Only assign role if member is approved, otherwise no role
          if (memberData.status === COOP_STATUS.APPROVED) {
            const role = memberData.role || 'cooperative Member';
            setCoopRole(role);
          } else {
            setCoopRole(null);
          }
        } else {
          setCoopRole(null);
        }
      } catch (err) {
        if (!isMounted) return;
        
        clearTimeout(timeoutId);
        
        // Don't break the UI for permission issues
        if (err.code === 'permission-denied') {
          console.warn('Permission denied for role fetch:', err);
        } else {
          console.error('Error fetching cooperative role:', err);
          setError(err.message);
        }
        setCoopRole(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCoopRole();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldFetch, currentUser?.uid, refreshTrigger]);

  // Helper function to check if user has a specific role
  const hasRole = (role) => {
    if (!shouldFetch) {
      return false;
    }
    return coopRole === role;
  };

  // Helper function to check if user has any of the specified roles
  const hasAnyRole = React.useCallback((roles) => {
    if (!shouldFetch || loading || !coopRole) {
      return false;
    }
    return Array.isArray(roles) && roles.includes(coopRole);
  }, [shouldFetch, loading, coopRole]);

  // Helper function to check if user is admin (exclusive role)
  const isAdmin = React.useCallback(() => {
    if (!shouldFetch) {
      return false;
    }
    return roleChecks.isAdmin;
  }, [shouldFetch, roleChecks.isAdmin]);

  // Helper function to check if user has financial roles (officers only, not admin)
  const isFinancial = React.useCallback(() => {
    if (!shouldFetch) {
      return false;
    }
    return roleChecks.isFinancial;
  }, [shouldFetch, roleChecks.isFinancial]);

  // Function to manually refresh the role data
  const refreshRole = React.useCallback(() => {
    if (!shouldFetch) {
      return;
    }
    setRefreshTrigger(prev => prev + 1);
  }, [shouldFetch]);

  return {
    coopRole,
    loading,
    error,
    hasRole,
    hasAnyRole,
    isAdmin,
    isFinancial,
    refreshRole
  };
}
